import {useQuery} from '@apollo/client';
import {useEffect, useState, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {GET_REPORT_OPERATORS, GetReportOperatorsResponse} from '../graphql/queries/getReportOperators';
import {getUserInfo, isReportDownloadSelfOnlyRole} from '../../../../utils/auth';
import {ReportFileFiltersType} from './useDeliveryReportFileQuery';

interface UseReportOperatorsResult {
    operatorOptions: {value: string, label: string}[];
    loading: boolean;
    defaultFilters: ReportFileFiltersType | null;
}

export const useReportOperators = (): UseReportOperatorsResult => {
    const {t, i18n} = useTranslation();
    const [operatorOptions, setOperatorOptions] = useState<{value: string, label: string}[]>([]);
    const [defaultFilters, setDefaultFilters] = useState<ReportFileFiltersType | null>(null);

    const { data: operatorsData, loading } = useQuery<GetReportOperatorsResponse>(GET_REPORT_OPERATORS);

    const allLabel = useMemo(() => {
        const translated = t('wms.download.filters.all');
        return translated === 'wms.download.filters.all' ? (i18n.language === 'zh' ? '全部' : 'All') : translated;
    }, [t, i18n.language]);

    useEffect(() => {
        if (operatorsData?.getReportOperators) {
            const selfOnly = isReportDownloadSelfOnlyRole();
            const mapped = operatorsData.getReportOperators.map(operator => ({
                value: operator.id,
                label: `${operator.name}`
            }));
            const options = selfOnly ? mapped : [{ value: '', label: allLabel }, ...mapped];
            setOperatorOptions(options);

            const currentUser = getUserInfo();
            let filters: ReportFileFiltersType = {
                type: undefined,
                name: undefined,
                reportDate: undefined,
                operatorId: undefined,
            };

            if (currentUser) {
                const currentUserId = parseInt(currentUser.account.id, 10);
                if (selfOnly) {
                    filters.operatorId = currentUserId;
                } else {
                    const currentOperator = operatorsData.getReportOperators.find(
                        operator => parseInt(operator.id, 10) === currentUserId
                    );
                    if (currentOperator) {
                        filters.operatorId = currentUserId;
                    }
                }
            }

            setDefaultFilters(filters);
        }
    }, [operatorsData, allLabel]);

    return {
        operatorOptions,
        loading,
        defaultFilters
    };
};